import "./style.scss";

import { Collapse } from "@mui/material";
import Payment from "assets/img/svg/navbar/payment";
import Wallet from "assets/img/svg/navbar/wallet";
import Paypal from "assets/img/svg/paypal";
import type { TEditingBeneficiarProfile } from "components/BPSettings/types";
import LabelComponent from "components/shared/LabelComponent";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import type { ICompany } from "store/settingsStore";
import type { IBeneficiariesCompanies } from "store/types";
import { defaultCountryFeatures, type CountryFeatures } from "types/countryFeatures";

type TProps = {
    data: TEditingBeneficiarProfile["payment"] | undefined;
    handleChange: (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, data: any) => void;
    companies?: IBeneficiariesCompanies[];
    acceptedMethods?: string[];
    company?: IBeneficiariesCompanies | ICompany | undefined;
    countryFeatures?: CountryFeatures;
    ibanBicErrors?: { iban?: string; swift?: string };
};

const BPPaymentMethod = ({
    data,
    handleChange,
    company,
    countryFeatures = defaultCountryFeatures,
    ibanBicErrors,
}: TProps) => {
    const [t] = useTranslation();
    const acceptedPaymentMethods: string[] = JSON.parse(
        company?.settings?.find((sttg: any) => sttg.name == "accepted_payment_methods")?.content ?? "[]",
    );

    const usesIban = countryFeatures.bankingFields.includes("iban");

    // For non-IBAN countries, build placeholder hints from validation rules
    const bankingFieldPlaceholder = (field: string): string => {
        const rule =
            countryFeatures.validation.bankingFields?.[field as keyof typeof countryFeatures.validation.bankingFields];
        if (!rule) return "";
        if ("length" in rule) return "0".repeat(rule.length ?? 0);
        if ("minLength" in rule) return `${"0".repeat(rule.minLength ?? 0)}–${"0".repeat(rule.maxLength ?? 0)}`;
        return "";
    };

    return (
        <div className="bp-settings-info bp-settings-info-payment">
            {acceptedPaymentMethods.includes("rib") && (
                <div className="bp-settings-info-bloc">
                    <div
                        className={`bp-settings-payment-options d-flex align-items-center justify-content-between${
                            data?.acceptedPayments.includes("rib") ? " active" : ""
                        }`}
                    >
                        <label className="d-flex align-items-center label-payment" htmlFor="payment-method-rib">
                            <Payment />
                            {t("NewBeneficiary.BankTransfer")}
                            {data?.acceptedPayments.includes("rib")
                                ? ` (${t("Beneficiars.Activated").toLowerCase()})`
                                : ""}
                        </label>
                        <input
                            type="checkbox"
                            name="acceptedPayments"
                            value="rib"
                            id="payment-method-rib"
                            checked={data?.acceptedPayments.includes("rib") ?? false}
                            onChange={(ev) => handleChange(ev, { ...data })}
                        />
                    </div>
                    <div
                        className={`${
                            data?.acceptedPayments.includes("rib") ? "no-collapsed-content" : "collapsed-content"
                        }`}
                    >
                        <LabelComponent htmlFor="ribOwnerName">{t("NewBeneficiary.OwnerName")}</LabelComponent>
                        <StyledTextField
                            placeholder={t("FormPlaceholders.FirstName")}
                            value={data ? data.ribOwnerName : ""}
                            name="ribOwnerName"
                            id="ribOwnerName"
                            sx={{ marginBottom: "16px" }}
                            onChange={(ev) => handleChange(ev, { ...data })}
                        ></StyledTextField>

                        {usesIban ? (
                            <>
                                <LabelComponent htmlFor="iban">{t("NewBeneficiary.Iban")}</LabelComponent>
                                <StyledTextField
                                    placeholder={t("FormPlaceholders.Iban")}
                                    value={data ? data.iban : ""}
                                    name="iban"
                                    id="iban"
                                    sx={{ marginBottom: "16px" }}
                                    error={!!ibanBicErrors?.iban}
                                    helperText={ibanBicErrors?.iban}
                                    onChange={(ev) => handleChange(ev, { ...data })}
                                ></StyledTextField>
                                <LabelComponent htmlFor="swift">{t("NewBeneficiary.Bic")}</LabelComponent>
                                <StyledTextField
                                    placeholder={t("FormPlaceholders.Bic")}
                                    value={data ? data.swift : ""}
                                    name="swift"
                                    id="swift"
                                    error={!!ibanBicErrors?.swift}
                                    helperText={ibanBicErrors?.swift}
                                    onChange={(ev) => handleChange(ev, { ...data })}
                                ></StyledTextField>
                            </>
                        ) : (
                            // Non-IBAN countries: show bankingMetadata fields driven by countryFeatures
                            countryFeatures.bankingFields.map((field) => (
                                <div key={field} style={{ marginBottom: "16px" }}>
                                    <LabelComponent htmlFor={field}>{t(`Beneficiary.${field}`)}</LabelComponent>
                                    <StyledTextField
                                        placeholder={bankingFieldPlaceholder(field)}
                                        value={data?.bankingMetadata?.[field] ?? ""}
                                        name={field}
                                        id={field}
                                        onChange={(ev) =>
                                            handleChange(
                                                {
                                                    target: {
                                                        name: "bankingMetadata",
                                                        value: { ...data?.bankingMetadata, [field]: ev.target.value },
                                                    },
                                                } as any,
                                                { ...data },
                                            )
                                        }
                                    ></StyledTextField>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
            {acceptedPaymentMethods.includes("paypal") && (
                <div className="bp-settings-info-bloc" style={{ paddingTop: "0.75rem" }}>
                    <div
                        style={{ paddingBottom: 0 }}
                        className={`bp-settings-payment-options d-flex align-items-center justify-content-between${
                            data?.acceptedPayments.includes("paypal") ? " active" : ""
                        }`}
                    >
                        <label
                            className="d-flex align-items-center paypal-option label-payment"
                            htmlFor="payment-method-paypal"
                        >
                            <Paypal />
                            {t("NewBeneficiary.Paypal")}
                            {data?.acceptedPayments.includes("paypal")
                                ? ` (${t("Beneficiars.Activated").toLowerCase()})`
                                : ""}
                        </label>
                        <input
                            type="checkbox"
                            name="acceptedPayments"
                            value="paypal"
                            id="payment-method-paypal"
                            checked={data?.acceptedPayments.includes("paypal") ?? false}
                            onChange={(ev) => handleChange(ev, { ...data })}
                        />
                    </div>
                    <Collapse in={data?.acceptedPayments.includes("paypal")}>
                        <LabelComponent htmlFor="paypalEmail">{t("NewBeneficiary.PaypalEmail")}</LabelComponent>
                        <StyledTextField
                            placeholder="example@crealo.app"
                            value={data ? data.paypalEmail : ""}
                            name="paypalEmail"
                            id="paypalEmail"
                            onChange={(ev) => handleChange(ev, { ...data })}
                        ></StyledTextField>
                    </Collapse>
                </div>
            )}
            {acceptedPaymentMethods.includes("check") && (
                <div className="bp-settings-info-bloc">
                    <div
                        className={`bp-settings-payment-options d-flex align-items-center justify-content-between${
                            data?.acceptedPayments.includes("check") ? " active" : ""
                        }`}
                    >
                        <label className="d-flex align-items-center label-payment" htmlFor="payment-method-check">
                            <Wallet />
                            {t("NewBeneficiary.Check")}
                            {data?.acceptedPayments.includes("check")
                                ? ` (${t("Beneficiars.Activated").toLowerCase()})`
                                : ""}
                        </label>
                        <input
                            type="checkbox"
                            name="acceptedPayments"
                            value="check"
                            id="payment-method-check"
                            checked={data?.acceptedPayments.includes("check") ?? false}
                            onChange={(ev) => handleChange(ev, { ...data })}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default BPPaymentMethod;
